import { Globe } from 'lucide-react';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';

import { buildCandidatePhotoPath } from '@/utils/candidatePhoto';
import { buildPoliticalMatchStoryCopy, sortPoliticalMatchMatchesByDistance } from '@/utils/political-match';
import { classNames } from '@/utils/classNames';
import type { PoliticalMatchCandidateMatch } from '@/models/political-match';

import styles from './PoliticalMatchShareStoryHtml.module.css';

import sharedStyles from '@/styles/positions.module.css';

type PoliticalMatchShareStoryProps = {
  economicScore: number | null;
  personalScore: number | null;
  matches: PoliticalMatchCandidateMatch[];
  featuredCandidateMatch?: PoliticalMatchCandidateMatch | null;
  userPhotoUrl?: string | null;
  includeCandidatePhoto?: boolean;
  className?: string;
};

type ImageWithFallbackProps = {
  src: string | null;
  alt: string;
  wrapperClassName: string;
  imageClassName?: string;
  fallback: ReactNode;
  style?: CSSProperties;
};

const STORY_HIGHLIGHT_PHRASES = [
  'mudança',
  'inclusão',
  'novos caminhos',
  'liberdade econômica',
  'liberdade pessoal',
  'liberdade de escolha',
  'menos interferência',
  'proteção social',
  'regras claras',
  'autonomia',
  'iniciativa',
  'independência',
  'flexibilidade',
  'cooperação',
  'eficiência',
  'resultados',
  'equilíbrio',
  'diálogo',
  'viabilidade',
  'síntese',
  'convívio',
  'ação pública',
  'ordem',
  'estabilidade',
  'previsibilidade',
  'instituições',
  'ação coletiva',
  'tradição',
  'responsabilidade',
  'preservar',
  'vida em comum',
  'caminho próprio',
];

const STORY_HIGHLIGHT_LOOKUP = new Set(STORY_HIGHLIGHT_PHRASES.map((phrase) => phrase.toLowerCase()));
const STORY_HIGHLIGHT_PATTERN = new RegExp(`(${STORY_HIGHLIGHT_PHRASES.map(escapeRegExp).join('|')})`, 'gi');
const STORY_BOLD_PATTERN = /\*\*(.+?)\*\*/g;

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function getStoryTitleFontSize(title: string): number {
  const normalizedLength = title.replace(/\s+/g, '').length;

  if (normalizedLength <= 12) {
    return 104;
  }

  if (normalizedLength <= 15) {
    return 98;
  }

  if (normalizedLength <= 18) {
    return 90;
  }

  if (normalizedLength <= 22) {
    return 82;
  }

  return 76;
}

function renderHighlightedText(text: string, keyPrefix = ''): ReactNode[] {
  const parts = text.split(STORY_HIGHLIGHT_PATTERN).filter(Boolean);

  return parts.map((part, index) => {
    const isHighlighted = STORY_HIGHLIGHT_LOOKUP.has(part.toLowerCase());
    const key = `${keyPrefix}${index}`;

    return isHighlighted ? (
      <span key={`${key}-accent`} className={styles.storyLeadAccent}>
        {part}
      </span>
    ) : (
      <span key={`${key}-plain`}>{part}</span>
    );
  });
}

function renderStoryLeadParagraph(text: string, keyPrefix: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  STORY_BOLD_PATTERN.lastIndex = 0;

  while ((match = STORY_BOLD_PATTERN.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(...renderHighlightedText(text.slice(lastIndex, match.index), `${keyPrefix}-plain-${nodes.length}-`));
    }

    nodes.push(
      <strong key={`${keyPrefix}-strong-${match.index}`} className={styles.storyLeadStrong}>
        {renderHighlightedText(match[1] ?? '', `${keyPrefix}-bold-${match.index}-`)}
      </strong>,
    );

    lastIndex = STORY_BOLD_PATTERN.lastIndex;
  }

  if (lastIndex < text.length) {
    nodes.push(...renderHighlightedText(text.slice(lastIndex), `${keyPrefix}-tail-${nodes.length}-`));
  }

  return nodes;
}

function ImageWithFallback({ src, alt, wrapperClassName, imageClassName, fallback, style }: ImageWithFallbackProps): JSX.Element {
  const [hasError, setHasError] = useState(false);
  const normalizedSource = src?.trim() ?? '';
  const showImage = normalizedSource.length > 0 && !hasError;

  useEffect(() => {
    setHasError(false);
  }, [normalizedSource]);

  return (
    <div className={wrapperClassName} style={style}>
      {showImage ? (
        <img
          className={imageClassName}
          src={normalizedSource}
          alt={alt}
          loading="eager"
          decoding="async"
          onError={() => setHasError(true)}
        />
      ) : (
        fallback
      )}
    </div>
  );
}

function StoryLogo(): JSX.Element {
  return (
    <ImageWithFallback
      src="/logos/logocompleta_sem_fundo.png"
      alt="Verdeconfirma"
      wrapperClassName={styles.storyBrandLogoWrap!}
      imageClassName={styles.storyBrandLogo!}
      fallback={<span className={styles.storyBrandFallback}>Verdeconfirma</span>}
    />
  );
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0] ?? '')
    .join('')
    .toUpperCase();
}

function StoryPhotoFallback({ mark }: { mark: string }): JSX.Element {
  return (
    <div className={styles.storyPhotoFallback}>
      <div className={styles.storyPhotoFallbackGlow} />
      <div className={styles.storyPhotoFallbackMark}>{mark}</div>
    </div>
  );
}

type StoryPhotoCardProps = {
  src: string | null;
  alt: string;
  tag: string;
  fallbackMark: string;
  variant?: 'default' | 'mirrored';
};

function StoryPhotoCard({ src, alt, tag, fallbackMark, variant = 'default' }: StoryPhotoCardProps): JSX.Element {
  return (
    <div className={classNames(styles.storyPhotoCard, variant === 'mirrored' && styles.storyPhotoCardMirrored)}>
      <ImageWithFallback
        src={src}
        alt={alt}
        wrapperClassName={styles.storyPhotoMedia!}
        imageClassName={styles.storyPhotoImage!}
        fallback={<StoryPhotoFallback mark={fallbackMark} />}
      />

      <div className={styles.storyPhotoOverlay} />
      <div className={styles.storyPhotoFrame} />
      <div className={styles.storyPhotoTag}>{tag}</div>
    </div>
  );
}
export const PoliticalMatchShareStory = forwardRef<HTMLDivElement, PoliticalMatchShareStoryProps>(function PoliticalMatchShareStory(
  props,
  forwardedRef,
): JSX.Element {
  const { economicScore, personalScore, matches, featuredCandidateMatch, userPhotoUrl, includeCandidatePhoto = true, className } = props;
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const posterRef = useRef<HTMLDivElement | null>(null);
  const [scale, setScale] = useState(1);

  const storyCopy = useMemo(() => buildPoliticalMatchStoryCopy(economicScore, personalScore), [economicScore, personalScore]);
  const titleSize = useMemo(() => getStoryTitleFontSize(storyCopy.title), [storyCopy.title]);
  const heroSource = userPhotoUrl?.trim() ? userPhotoUrl.trim() : null;
  const orderedMatches = useMemo(
    () => sortPoliticalMatchMatchesByDistance(matches, economicScore, personalScore),
    [economicScore, matches, personalScore],
  );
  const featuredMatch = featuredCandidateMatch === undefined ? orderedMatches[0] ?? null : featuredCandidateMatch;
  const showCandidatePhoto = includeCandidatePhoto && featuredMatch !== null;
  const featuredMatchSource = showCandidatePhoto && featuredMatch ? buildCandidatePhotoPath(featuredMatch.candidate.slug) : null;

  useImperativeHandle(forwardedRef, () => posterRef.current as HTMLDivElement, []);

  useLayoutEffect(() => {
    const viewport = viewportRef.current;

    if (!viewport) {
      return;
    }

    const updateScale = () => {
      const nextScale = Math.max(0.18, Math.min(1, viewport.clientWidth / 1080));
      setScale(nextScale);
    };

    updateScale();

    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    const observer = new ResizeObserver(() => updateScale());
    observer.observe(viewport);

    return () => observer.disconnect();
  }, []);

  const viewportStyle = {
    '--story-scale': scale.toFixed(4),
  } as CSSProperties;

  const titleStyle = {
    '--story-title-size': `${titleSize}px`,
  } as CSSProperties;

  const heroTag = 'Você';
  const heroFallbackMark = 'VC';
  const featuredMatchTag = featuredMatch?.candidate.name ?? 'Seu match mais próximo';
  const featuredMatchFallbackMark = featuredMatch ? getInitials(featuredMatch.candidate.name) : 'PM';

  return (
    <div ref={viewportRef} className={classNames(sharedStyles.shareStoryCanvas, styles.storyViewport, className)} style={viewportStyle} aria-hidden="true">
      <div className={styles.storyStage}>
        <div ref={posterRef} className={styles.storySheet}>
          <div className={styles.storyBackdropTop} aria-hidden="true" />
          <div className={styles.storyBackdropBottom} aria-hidden="true" />
          <div className={styles.storyBackdropV} aria-hidden="true">
            V
          </div>

          <header className={styles.storyHeader}>
            <StoryLogo />
          </header>

          <main className={styles.storyMain}>
            <div>
              <p className={styles.storyEyebrow}>{storyCopy.eyebrow}</p>
              <h1 className={styles.storyTitle} style={titleStyle}>
                {storyCopy.title}
              </h1>
            </div>

            <aside className={styles.storyPhotoColumn}>
              <StoryPhotoCard src={heroSource} alt="Sua foto" tag={heroTag} fallbackMark={heroFallbackMark} />

              {showCandidatePhoto ? (
                <StoryPhotoCard
                  src={featuredMatchSource}
                  alt={featuredMatch ? `Foto de ${featuredMatch.candidate.name}` : 'Foto do político mais próximo'}
                  tag={featuredMatchTag}
                  fallbackMark={featuredMatchFallbackMark}
                  variant="mirrored"
                />
              ) : null}
            </aside>

            <section className={styles.storyCopyColumn}>
              <div className={styles.storySite}>
                <span>Descubra o seu match político em</span>
                <Globe size={18} strokeWidth={2.3} />
                <span>verdeconfirma.com.br</span>
              </div>
              <div className={styles.storyLead}>
                {storyCopy.description.map((paragraph, index) => (
                  <p key={`${storyCopy.title}-${index}`} className={styles.storyLeadParagraph}>
                    {renderStoryLeadParagraph(paragraph, `${storyCopy.title}-${index}`)}
                  </p>
                ))}
              </div>
              
            </section>
          </main>
        </div>
      </div>
    </div>
  );
});
